import { Handle, Position, type NodeProps } from '@xyflow/react';
import './LineRow.css';

/**
 * ZAHLUNGSAVIS (payout_advice_event) tile rendered as the 5th column
 * (right of ABRECHNUNG) in the Gutschrift-Check canvas (#793 phase 4,
 * scope addition 2026-05-20).
 *
 * The Zahlungsavis is a payment notification bundling N existing
 * Gutschriften under one bank transfer — semantically distinct from
 * the Periodenabrechnung even though the visual is similar. Header:
 *
 *   - ``Zahlungsavis #<avis_number> · <total_payment_amount>`` when
 *     the sender carries an Avis-Nr.
 *   - ``Zahlungsavis Konto <konto>`` when the sender carries a Konto-
 *     Nr. instead (per the 2026-05-20 FRUTANiA corpus sample). The
 *     composer maps either into the same ``number`` field; the FE
 *     just renders whichever is present.
 *
 * Strict real-only — no placeholder when the GS is unmatched (§E.5);
 * the "Zahlungsavis fehlt" bucket ships in v1 as 0 (the "paid"
 * signal hunt is a follow-up).
 *
 * Single ``Handle`` on the LEFT edge so the GS's ``referenz-source``
 * arrow lands on the inside-facing edge — same convention as
 * PayoutStatementNode (sibling column to the right of GS).
 */
export interface PayoutAdviceNodeData {
  entityId: string;
  number: string | null;
  totalPaymentAmount: number | null;
  paymentDueDate: string | null;
  buyerName: string | null;
  sellerName: string | null;
  // #844 — keyboard-focus ring (see LineRowNodeData.isFocused).
  isFocused?: boolean;
  [key: string]: unknown;
}

const eurFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

export function PayoutAdviceNode({ data }: NodeProps) {
  const d = data as PayoutAdviceNodeData;
  const titleText = d.number ? `Zahlungsavis #${d.number}` : 'Zahlungsavis';
  const totalText = d.totalPaymentAmount != null ? eurFmt.format(d.totalPaymentAmount) : null;
  // buyer = Vermarkter (Zahlungspflichtiger, the payer); seller = farm
  // (Empfänger). Show buyer first (same fallback shape as PayoutStatement).
  const orgText = d.buyerName ?? d.sellerName ?? null;
  return (
    <div
      className={`line-row-node line-row-node--bestellung${d.isFocused ? ' line-row-node--focused' : ''}`}
      data-testid={`payout-advice-tile-${d.entityId}`}
      style={{
        borderColor: '#9ca3af',
        background: '#fafafa',
      }}
    >
      <Handle id="title-target" type="target" position={Position.Left} />
      <div className="line-row-node__header">
        <span className="line-row-node__doc-number">{titleText}</span>
        {totalText && <span className="line-row-node__date">{totalText}</span>}
      </div>
      {d.paymentDueDate && (
        <div className="line-row-node__orgs">
          <span className="line-row-node__supplier">Zahlung am {d.paymentDueDate}</span>
        </div>
      )}
      {orgText && (
        <div className="line-row-node__orgs">
          <span className="line-row-node__supplier">{orgText}</span>
        </div>
      )}
    </div>
  );
}
